import { Button, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import cn from 'classnames';
import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { IChatConversation } from 'src/@types/chat';
import { PageContainer } from 'src/components/PageContainer';
import EmptyContent from 'src/components/empty-content';
import Iconify from 'src/components/iconify';
import Logo from 'src/components/logo';
import { ConnectionStatusIcon } from 'src/features/RTCBar/ConnectionStatusIcon';
import { CustomChannelTitle } from 'src/features/RTCBar/styles';
import { Roles } from 'src/features/workspace/WorkspaceShareMemberRoleSelectProps';
import { useBreakpoints } from 'src/hooks/useBreakpoints';
import { useLocales } from 'src/locales';
import { useVoiceChannelsMembers } from 'src/provider/VoiceChannel';
import { useVoiceChannelContext } from 'src/provider/VoiceChannel/VoiceChannelContext';
import { useLivekitRTCState } from 'src/provider/VoiceChannel/livekit/useLivekitRTCState';
import { ChatHeader } from '../ChatHeader';
import { useChatAllowedAction } from '../shared/useChatAllowedAction';
import { VoiceChannelControls } from './VoiceChannelControls';
import { VoiceChannelMembersGrid } from './VoiceChannelMembersGrid';

type Props = {
  conversation: IChatConversation;
  isGuest?: boolean;
};

export const ChatVoiceChannel = ({ conversation, isGuest = false }: Props) => {
  const navigate = useNavigate();
  const { translate } = useLocales();

  const { voiceMembers } = useVoiceChannelsMembers(conversation, true);

  const { connectVoiceChannel, voiceChannels } = useVoiceChannelContext();

  const {
    activeVoiceChannel,
    connectionStatus,
    connectionQuality,
    updateLocalParticipant,
  } = useLivekitRTCState();

  const userPermissions = useChatAllowedAction({ conversation });

  const channelStatus = useMemo(() => {
    const members = voiceMembers.filter((m) => m.status === 'connected');
    if (!members.length || connectionStatus !== 'disconnected')
      return translate('communicate.voice_channel.status.no_one_in voice');
    if (members.length === 1) {
      return translate(
        'communicate.voice_channel.status.one_is_currently_in_voice',
        { name: members[0].user.name },
      );
    }
    if (members.length === 2) {
      return translate(
        'communicate.voice_channel.status.two_is_currently_in_voice',
        {
          firstMember: members[0].user.name,
          secondMember: members[1].user.name,
        },
      );
    }
    return translate(
      'communicate.voice_channel.status.group_is_currently_in_voice',
      {
        firstMember: members[0].user.name,
        secondMember: members[1].user.name,
        membersCount: members.length - 2,
      },
    );
  }, [voiceMembers, connectionStatus, translate]);

  const voiceChannel = useMemo(() => {
    return voiceChannels.find((vc) => vc.channel._id === conversation._id);
  }, [voiceChannels, conversation._id]);

  const { upMobile } = useBreakpoints();

  const hasTheaterMode = useMemo(
    () => upMobile && voiceMembers.some((member) => member.theaterMode),
    [upMobile, voiceMembers],
  );

  const backToRoom = () => {
    const externalLink = sessionStorage.getItem('externalLink');
    if (externalLink) {
      navigate(`/gathering/lobby/${externalLink}`, { replace: true });
    }
  };

  const handleConnectVoiceChannel = (
    channel: IChatConversation,
    isGuest: boolean,
  ) => {
    connectVoiceChannel({ channel, isGuest });
    const role = isGuest ? Roles.VIEWER : userPermissions.role || Roles.MEMBER;
    updateLocalParticipant({ role });
  };

  const isConnected = useMemo(
    () =>
      activeVoiceChannel?.channel?._id === conversation._id &&
      connectionStatus === 'connected',
    [connectionStatus, activeVoiceChannel?.channel?._id, conversation._id],
  );

  return (
    <PageContainer
      sx={{ maxWidth: '100%', overflowX: 'hidden' }}
      className={cn({ fullHeight: isGuest })}
    >
      {!isGuest && <ChatHeader conversation={conversation} />}

      <Stack
        justifyContent={isConnected ? 'space-between' : 'flex-start'}
        flexGrow={1}
        overflow="hidden"
      >
        {(!isGuest || isConnected) && (
          <VoiceChannelMembersGrid
            conversationId={conversation._id}
            isGuest={isGuest}
            participants={voiceMembers}
            sx={{ ...(isGuest && { padding: 3, gap: 1.5 }) }}
          />
        )}

        {connectionStatus === 'disconnected' ||
        activeVoiceChannel?.channel?._id !== conversation._id ? (
          <Stack
            alignItems="center"
            justifyContent="center"
            gap={2}
            flexGrow={!isGuest && voiceMembers.length ? 'unset' : 1}
            overflow="auto"
            pb={3}
          >
            {isGuest ? (
              <>
                {connectionStatus === 'disconnected' ? (
                  <>
                    <CustomLogoContainer>
                      <Logo disabledLink implicitTheme="dark" />
                      <Typography variant="h3">copera.ai</Typography>
                    </CustomLogoContainer>
                    <Typography variant="h4">
                      {translate(
                        'communicate.join_conference.you_left_meeting',
                      )}
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => backToRoom()}
                    >
                      {translate('communicate.join_conference.back_to_room')}
                    </Button>
                  </>
                ) : (
                  <>
                    <CustomLogoContainer>
                      <Logo disabledLink implicitTheme="dark" />
                      <Typography variant="h3">copera.ai</Typography>
                    </CustomLogoContainer>
                    <Typography variant="h4">
                      {translate(
                        'communicate.join_conference.connecting_to_room',
                        { name: conversation.name },
                      )}
                    </Typography>
                  </>
                )}
              </>
            ) : (
              <>
                {!voiceMembers.length && (
                  <EmptyContent
                    title={conversation.name}
                    img="/assets/illustrations/illustration_empty_voice.svg"
                    sx={{
                      py: 2,
                    }}
                  />
                )}

                <Typography variant="subtitle1">{channelStatus}</Typography>

                {!!voiceChannel?.lockedBy && (
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="center"
                    gap={1}
                    py={2}
                  >
                    <Iconify icon="tabler:lock-filled" color="warning.main" />
                    <Typography variant="h6">
                      {translate(
                        'communicate.voice_channel.voice_channel_is_locked',
                      )}
                    </Typography>
                  </Stack>
                )}

                <Button
                  variant="contained"
                  color="primary"
                  disabled={!!voiceChannel?.lockedBy}
                  onClick={() =>
                    handleConnectVoiceChannel(conversation, isGuest)
                  }
                >
                  {translate('shared.buttons.join_channel')}
                </Button>
              </>
            )}
          </Stack>
        ) : (
          <VoiceFooter className={cn({ isGuest })}>
            {isGuest && (
              <GatheringGuestInfoStyled>
                <ConnectionStatusIcon
                  connectionStatus={connectionStatus}
                  connectionQuality={connectionQuality || 0}
                />
                {connectionStatus === 'connected' && (
                  <CustomChannelTitle variant="subtitle2" color="success.main">
                    {conversation?.name}
                  </CustomChannelTitle>
                )}
              </GatheringGuestInfoStyled>
            )}
            <VoiceControlsContainer className={cn({ isGuest })}>
              <VoiceChannelControls
                floating={hasTheaterMode}
                isGuest={isGuest}
              />
            </VoiceControlsContainer>
            <div></div>
          </VoiceFooter>
        )}
      </Stack>
    </PageContainer>
  );
};

const VoiceFooter = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(0),
  '&.isGuest': {
    justifyContent: 'space-between',
  },
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(0, 2, 2),
  },
}));

const VoiceControlsContainer = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1,
  [theme.breakpoints.down('sm')]: {
    '&.isGuest': {
      position: 'fixed',
      bottom: 0,
      left: 0,
      zIndex: theme.zIndex.modal,
    },
  },
}));

const GatheringGuestInfoStyled = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1,
  gap: theme.spacing(0.5),
}));

const CustomLogoContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
}));
